import type { PageTargeting } from "../../config/page";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import { getFandomContext } from "../../helpers/getFandomContext";
import { isMobile } from "../../helpers/isMobile";
import type { FandomLegacyContextTargeting, TaxonomyTags } from "common-types";
import {
  getExpBucket,
  getHostnamePrefix,
  getTopLevelDomain,
  getWikiId,
} from "utils";
import {
  hasFeaturedVideoOnPage,
  isIncontentPlayerApplicable,
} from "../../helpers/hasVideoOnPage";
import { getServerSideExperimentName } from "./getServerSideExperimentName";
import type { AdEngine } from "../../core/AdEngine";
import type { GptWrapper } from "../../core/GptWrapper";
import { getBrowserName } from "../../helpers/getBrowserName";
import { getVersion } from "../../helpers/version";
import { getMonetizationTier } from "../../helpers/getMonetizationTier";

/**
 * PageLevelTargetingPlugin is responsible for setting up the page-level targeting in GPT.
 * It reads global variables (like `fandomContext`) and sets up the targeting based on them using the `onInitPageTargeting` hook.
 */
export class PageLevelTargetingPlugin extends AdEnginePlugin {
  constructor(private readonly activeLabradorKeyValues?: string[]) {
    super();
  }

  /**
   * Creates the rating keyvalue and returns it. Rating is a combined array of
   * {@link TaxonomyTags.esrb} and {@link TaxonomyTags.mpa} with each string value prefixed with the
   * group it's from. IE: all tags from esrb are prefixed with `esrb:`.
   *
   * For example with the following call:
   * `getRating({ esrb: ["e10", "everyone", "teen"], mpa: ["pg13"]})` the function will return
   * `["esrb:e10", "esrb:everyone", "esrb:teen", "mpa:pg13"]`
   *
   * @param ratingTaxonomyTags The taxonomy tags object containing the esrb and mpa rating values.
   * @returns a single array of all values from esrb and mpa inputs prefixed by the group they
   * came from.
   */
  private getRating({
    esrb = [],
    mpa = [],
  }: Pick<TaxonomyTags, "esrb" | "mpa">) {
    const ratings = [];
    for (const rating of esrb) {
      ratings.push(`esrb:${rating}`);
    }
    for (const rating of mpa) {
      ratings.push(`mpa:${rating}`);
    }

    return ratings;
  }

  /**
   * Calculates and returns the s2 keyvalue which is `pageType` with a prefix or suffix depending on
   * functionality on the page like if there is a featured video or incontent video.
   * @returns the s2 value such as `wv-article`, `home`, or `article`.
   */
  private getS2PageType(targeting: FandomLegacyContextTargeting) {
    const pageType = targeting?.pageType ?? "article";
    if (pageType === "article") {
      // Check if the hasFeaturedVideo property is on the page and the canPlayVideo
      // function is defined and returns true. If so, we have a video so amend the
      // page type for the Ad Unit Path to be prepended with `wv`.
      if (hasFeaturedVideoOnPage(targeting)) {
        return `wv-${pageType}`;
      }

      /**
       * This function is checking if the page type for an article should be adjusted to
       * `article-ic`. This is critical for getting some targeting on the page to
       * work, especially house ads, which target `s2 is not 'article'.
       */
      if (isIncontentPlayerApplicable()) {
        return `${pageType}-ic`;
      }
    }

    return pageType;
  }

  /**
   * Generates the keyvalues derived from data in `window.ads.context`.
   *
   * @returns object with the keyvalues created from `window.ads.context
   */
  private getTargetingFromLegacyAdsContext(): PageTargeting {
    const { targeting = {}, opts = {} } = window.ads?.context ?? {};

    return {
      s2: this.getS2PageType(targeting),
      original_host: opts.isGamepedia === true ? "gamepedia" : "fandom",
    };
  }

  /**
   * Generates the keyvalues derived from data in `window.fandomContext.site` object.
   *
   * @returns object with the keyvalues created from `window.fandomContext.site`
   */
  private getTargetingFromFandomContextSite(): PageTargeting {
    const fandomContext = getFandomContext();

    if (!fandomContext?.site) {
      return {};
    }

    const { site } = fandomContext;
    const targeting: PageTargeting = {};
    const {
      categories,
      siteName = "wikia",
      tags,
      directedAtChildren,
      taxonomy = [],
    } = site;

    if (site.top1000) {
      targeting.top = "1k";
    }

    return {
      ...targeting,
      s0: taxonomy[0],
      s0c: categories,
      s0v: taxonomy[1],
      s1: `_${siteName}`,
      kid_wiki: directedAtChildren ? "1" : "0",
      rating: this.getRating(tags ?? {}),
      ...tags,
    };
  }

  /**
   * Generates the keyvalues derived from data in `window.fandomContext.page` object
   *
   * @returns object with the keyvalues created from `window.fandomContext.page`
   */
  private getTargetingFromFandomContextPage(): PageTargeting {
    const fandomContext = getFandomContext();

    if (!fandomContext?.page) {
      return {};
    }

    const { page } = fandomContext;
    const { articleId, pageName, pageType, pageId, lang, wordCount } = page;

    return {
      artid: String(articleId),
      lang,
      pageId: pageId.toString(),
      pageName: pageName,
      pageType: pageType,
      tier: getMonetizationTier(fandomContext).toString(),
      word_count: String(wordCount),
      wpage: pageName?.toLowerCase(),
    };
  }

  /**
   * Generates the keyvalues derived from data in `window.fandomContext.tracking` object
   * This object may not be available immediately, Identity Engine sets it only after receiving consent
   *
   * @returns object with the keyvalues created from `window.fandomContext.tracking`
   */
  private getTargetingFromFandomContextTracking(): PageTargeting {
    const fandomContext = getFandomContext();

    if (!fandomContext?.tracking) {
      return {};
    }

    const { tracking } = fandomContext;
    const result: PageTargeting = {};

    if (tracking.pvNumber !== undefined) {
      result.pv = tracking.pvNumber.toString();
    }

    if (tracking.pvNumberGlobal !== undefined) {
      result.pvg = tracking.pvNumberGlobal.toString();
    }

    if (tracking.pvUID !== undefined) {
      result.pvuid = tracking.pvUID;
    }

    if (tracking.ppid !== undefined) {
      result.ppid = tracking.ppid;
    }

    return result;
  }

  /**
   * Generates the keyvalues derived from data in `window.fandomContext.targeting` object
   * This object may not be available immediately, Identity Engine sets it only after receiving consent
   *
   * @returns object with the keyvalues created from `window.fandomContext.targeting`
   */
  private getTargetingFromFandomContextTargeting(): PageTargeting {
    const fandomContext = getFandomContext();

    if (!fandomContext?.targeting) {
      return {};
    }

    const { targeting } = fandomContext;
    const result: PageTargeting = {};

    if (targeting.brand !== undefined) {
      result.brand = targeting.brand;
    }

    if (targeting.adGroup !== undefined) {
      result.adGroup = targeting.adGroup.split(",");
    }

    return result;
  }

  /**
   * Creates all the page targeting key-values that rely on information stored on the
   * `window.fandomContext` object.
   *
   * @returns subset of page key-values derived from fandomContext
   */
  private getTargetingFromFandomContext(): PageTargeting {
    return {
      ...this.getTargetingFromFandomContextPage(),
      ...this.getTargetingFromFandomContextSite(),
      ...this.getTargetingFromFandomContextTracking(),
      ...this.getTargetingFromFandomContextTargeting(),
    };
  }

  /**
   * Returns information about the server-side experiment
   *
   * @return array of experiment groups
   */
  private getExperimentGroups(): string[] {
    const experimentName = getServerSideExperimentName();
    return experimentName ? [experimentName] : [];
  }

  /**
   * Creates and returns an object with key values that are derived from window.fandomHost object
   * like geo and device.
   *
   * @returns object with key values derived from window.fandomHost object
   */
  private getTargetingFromFandomHost(): PageTargeting {
    const isMobilePage = isMobile();
    return {
      device: window.fandomHost?.device ?? "desktop",
      geo: window.fandomHost?.geo?.country ?? "none",
      is_mobile: isMobilePage ? "1" : "0",
      skin: `ucp_${isMobilePage ? "mobile" : "desktop"}`,
      browser: getBrowserName(),
    };
  }

  hooks: Hooks = {
    onInitPageTargeting: (initialPageTargeting: PageTargeting) => {
      const defaultTargeting: PageTargeting = {
        odyssey: ["1", `1_${getWikiId()}`],
        odyssey_version: getVersion() ?? "unknown",
        product: "fandom",
        uap: "none",
        uap_c: "none",
        labrador: this.activeLabradorKeyValues,
        dmn: getTopLevelDomain().replace(/\./g, ""),
        hostpre: getHostnamePrefix() ?? undefined,
        exp_bucket: getExpBucket(),
        experiment_groups: this.getExperimentGroups(),
      };

      Object.assign(
        initialPageTargeting,
        defaultTargeting,
        this.getTargetingFromFandomContext(),
        this.getTargetingFromFandomHost(),
        this.getTargetingFromLegacyAdsContext(),
      );
    },
    /**
     * Some data may be undefined at the beginning (e.g. set after consent),
     * so they have to be set later
     */
    onBiddersInit: (adEngine: AdEngine) => {
      const targeting = {
        ...this.getTargetingFromFandomContextTracking(),
        ...this.getTargetingFromFandomContextTargeting(),
      };

      Object.entries(targeting).forEach(([key, value]) => {
        adEngine.gptWrapper.setTargeting(key, value);
      });
    },

    onBeforeFirstCallAdsRequest: (gptWrapper: GptWrapper) => {
      const { ppid } = this.getTargetingFromFandomContextTracking();

      if (ppid) {
        gptWrapper.updatePublisherProvidedId(ppid);
      }
    },
  };
}
